import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import * as Sentry from '@sentry/browser';

import { RequestAccessAPI } from '../index';
import { directoryType } from '../types';
import { DIRECTORY_TYPE } from '../../constants';
import { useFetchTranslations } from './useFetchTranslations';
import { useEffect } from 'react';

const useFetchRequestAccessForm = (directoryType: directoryType = DIRECTORY_TYPE.GOOGLE) => {
    const { accessId = '' } = useParams();

    const {
        data: requestAccessFormData,
        isLoading: isLoadingRequestAccessFormData,
        error: errorRequestAccessFormData,
    } = useQuery({
        queryKey: ['requestAccessForm', directoryType, accessId],
        queryFn: () => RequestAccessAPI.fetchRequestAccessForm(directoryType, accessId),
    });

    const language = requestAccessFormData?.language;

    const {
        data: translations,
        isLoading: isLoadingTranslations,
        error: errorTranslations,
    } = useFetchTranslations(language);

    useEffect(() => {
        if (errorRequestAccessFormData) {
            Sentry.captureMessage(
                JSON.stringify({
                    title: `RequestAccessAPI.fetchRequestAccessForm(${directoryType}, ${accessId})`,
                    data: requestAccessFormData,
                    error: errorRequestAccessFormData,
                }),
                'error'
            );
        }
    }, [accessId, directoryType, errorRequestAccessFormData, requestAccessFormData]);

    useEffect(() => {
        if (errorTranslations) {
            Sentry.captureMessage(
                JSON.stringify({
                    title: `useFetchTranslations(${language})`,
                    data: translations,
                    error: errorTranslations,
                }),
                'error'
            );
        }
    }, [errorTranslations, language, translations]);

    return {
        isLoading: isLoadingRequestAccessFormData || isLoadingTranslations,
        isErrorAccessIdNotFound: errorRequestAccessFormData?.message.includes('404'),
        error: errorRequestAccessFormData || errorTranslations,
        data: requestAccessFormData,
        translations,
    };
};

export { useFetchRequestAccessForm };
